// Visitor tracking utility functions for analytics
import { supabase } from "../supabaseClient";
import { generateTrackingId, setTrackingCookie, getVisitorId } from "./trackingCore";
import logger from "./logger";

/**
 * Generate a unique visitor ID (delegates to shared trackingCore).
 * @returns {string} Unique visitor ID
 */
export const generateVisitorId = () => generateTrackingId();

/**
 * Set visitor ID cookie with secure options.
 * Always stores in localStorage; cookie only if consent given.
 * @param {string} visitorId - Unique visitor ID
 */
export const setVisitorCookie = (visitorId) => {
  localStorage.setItem('visitorId', visitorId);
  setTrackingCookie('visitorId', visitorId, 31536000); // 1 year
};

/**
 * Get visitor ID from cookie or localStorage.
 * @returns {string|null} Visitor ID or null if not found
 */
export const getVisitorIdFromCookie = () => getVisitorId();

/**
 * Initialize visitor tracking - creates new ID if doesn't exist
 * @returns {string} Visitor ID
 */
export const initializeVisitorTracking = () => {
  let visitorId = getVisitorIdFromCookie();

  if (!visitorId) {
    // New visitor - generate and set unique ID
    visitorId = generateVisitorId();
    setVisitorCookie(visitorId);

    // Track first visit

    // You can add analytics tracking here later
    // trackEvent('first_visit', { visitorId });

    return visitorId;
  } else {
    // Returning visitor

    // Refresh localStorage backup if missing
    if (!localStorage.getItem('visitorId')) {
      localStorage.setItem('visitorId', visitorId);
    }

    return visitorId;
  }
};

/**
 * Get current visitor ID (for use throughout the app)
 * @returns {string|null} Current visitor ID
 */
export const getCurrentVisitorId = () => {
  return getVisitorIdFromCookie();
};

/**
 * Track page view with visitor ID
 * @param {string} pageName - Name of the page
 * @param {object} additionalData - Additional tracking data
 */
export const trackPageView = (pageName, additionalData = {}) => {
  const visitorId = getCurrentVisitorId();

  if (visitorId) {

    // Here you can add your analytics service integration
    // For example: analytics.track('page_view', { page: pageName, visitorId, ...additionalData });
  }
};

/**
 * Log analytics event to Supabase PageEvent table
 * @param {string} type - Event type (PAGE_VIEW, SEARCH_QUERY, FILTER, EVENT_VIEW)
 * @param {string} value - Event value (depends on type)
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const logAnalyticsEvent = async (type, value, userId = null) => {
  try {
    const visitorId = getCurrentVisitorId();

    if (!visitorId) {
      logger.warn('No visitor ID available for analytics tracking');
      return;
    }

    const { error } = await supabase
      .from('PageEvent')
      .insert({
        type,
        value,
        user_cookie: visitorId,
        user_id: userId
      });

    if (error) {
      logger.error('Error logging analytics event:', error);
    } else {
    }
  } catch (error) {
    logger.error('Error in logAnalyticsEvent:', error);
  }
};

/**
 * Track page view event to Supabase
 * @param {string} pageValue - Page value (GENERAL, EVENTS, DEALS, COMMUNITY_CHATS, ACCOUNT, SPIN_WIN)
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const trackPageViewAnalytics = async (pageValue, userId = null) => {
  await logAnalyticsEvent('PAGE_VIEW', pageValue, userId);
};

/**
 * Track search query event to Supabase.
 * Logs as SEARCH_QUERY_ZERO when resultCount === 0 (unmet demand),
 * otherwise SEARCH_QUERY. Keeps the query string as the event value.
 * @param {string} searchQuery - The search term
 * @param {string|null} userId - Logged in user ID (nullable)
 * @param {number|null} resultCount - Number of results returned (null => legacy SEARCH_QUERY)
 */
export const trackSearchQueryAnalytics = async (searchQuery, userId = null, resultCount = null) => {
  const type = resultCount === 0 ? 'SEARCH_QUERY_ZERO' : 'SEARCH_QUERY';
  await logAnalyticsEvent(type, searchQuery, userId);
};

/**
 * Track filter event to Supabase
 * @param {string} filterType - Filter type (CITY, PRICE, DATE, VENUE, EVENT_TYPE, GENRE, AGE)
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const trackFilterAnalytics = async (filterType, userId = null) => {
  await logAnalyticsEvent('FILTER', filterType, userId);
};

/**
 * Track when a user clicks an event card
 */
export const trackEventClickAnalytics = async (eventId, userId = null) => {
  await logAnalyticsEvent('EVENT_CLICK', String(eventId), userId);
};

/**
 * Track when an event card enters the viewport on the homepage.
 * Writes to event_impressions_daily (daily counter) instead of PageEvent rows.
 */
export const trackEventInViewAnalytics = async (eventId, userId = null) => {
  await supabase.rpc('increment_event_impression', { p_event_id: Number(eventId) });
};

/**
 * Track when a user shares an event
 */
export const trackEventShareAnalytics = async (eventId, userId = null) => {
  await logAnalyticsEvent('EVENT_SHARE', String(eventId), userId);
};

/**
 * Track event view to Supabase
 * @param {string} eventId - The event ID
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const trackEventViewAnalytics = async (eventId, userId = null) => {
  await logAnalyticsEvent('EVENT_VIEW', eventId, userId);
};

/**
 * Deal analytics helpers (mirror of event helpers)
 */
export const trackDealClickAnalytics = async (dealId, userId = null) => {
  await logAnalyticsEvent('DEAL_CLICK', String(dealId), userId);
};

export const trackDealInViewAnalytics = async (dealId, userId = null) => {
  await logAnalyticsEvent('DEAL_IN_VIEW', String(dealId), userId);
};

export const trackDealShareAnalytics = async (dealId, userId = null) => {
  await logAnalyticsEvent('DEAL_SHARE', String(dealId), userId);
};

export const trackDealViewAnalytics = async (dealId, userId = null) => {
  await logAnalyticsEvent('DEAL_VIEW', String(dealId), userId);
};

/**
 * Enhanced button click tracking with comprehensive metadata
 * @param {Object} buttonData - Button interaction data
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const trackButtonClickAnalytics = async (buttonData, userId = null) => {
  try {
    const visitorId = getCurrentVisitorId();

    if (!visitorId) {
      logger.warn('No visitor ID available for button click tracking');
      return;
    }

    const { error } = await supabase
      .from('ButtonClickEvent')
      .insert({
        element_type: buttonData.elementType,
        element_text: buttonData.elementText,
        element_id: buttonData.elementId,
        element_classes: buttonData.elementClasses,
        page_path: buttonData.pagePath,
        page_title: buttonData.pageTitle,
        parent_component: buttonData.parentComponent,
        click_coordinates: buttonData.clickCoordinates,
        viewport_size: buttonData.viewportSize,
        user_agent: buttonData.userAgent,
        metadata: buttonData.metadata,
        user_cookie: visitorId,
        user_id: userId
      });

    if (error) {
      // Fail silently — table may not exist yet or RLS may block inserts
      // Only log in development for debugging
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[ButtonTracking] Insert skipped:', error.message);
      }
    }
  } catch (error) {
    // Fail silently — tracking should never break the app
  }
};

/**
 * Smart element detection - determines if an element is clickable
 * @param {Element} element - DOM element to check
 * @returns {boolean} Whether the element is considered clickable
 */
export const isClickableElement = (element) => {
  if (!element) return false;

  const tagName = element.tagName.toLowerCase();
  const hasOnClick = element.onclick || element.getAttribute('onclick');
  const hasRole = element.getAttribute('role');
  const hasCursor = window.getComputedStyle(element).cursor === 'pointer';

  // Definite clickable elements
  const clickableTags = ['button', 'a', 'input', 'select', 'textarea'];
  if (clickableTags.includes(tagName)) return true;

  // Elements with click handlers or cursor pointer
  if (hasOnClick || hasCursor) return true;

  // ARIA roles that indicate clickability
  const clickableRoles = ['button', 'link', 'tab', 'menuitem', 'option'];
  if (hasRole && clickableRoles.includes(hasRole)) return true;

  // Common class patterns that indicate buttons
  // Normalize class names across HTML and SVG elements
  // - element.classList for reliable stringification
  // - SVG elements may expose className as SVGAnimatedString with baseVal
  // - Fallback to getAttribute('class') or empty string
  const classNames = element.classList && typeof element.classList.forEach === 'function'
    ? Array.from(element.classList).join(' ')
    : (element.getAttribute && element.getAttribute('class'))
      || (typeof element.className === 'string' ? element.className : (element.className && element.className.baseVal) || '');
  const buttonClassPatterns = [
    'btn', 'button', 'click', 'submit', 'premium-button',
    'white-button', 'close-button', 'openPopupButtons'
  ];

  for (const pattern of buttonClassPatterns) {
    if (classNames.includes(pattern)) return true;
  }

  return false;
};

/**
 * Extract meaningful text content from element
 * @param {Element} element - DOM element
 * @returns {string} Cleaned text content
 */
export const getElementText = (element) => {
  if (!element) return '';

  // For images, use alt text
  if (element.tagName.toLowerCase() === 'img') {
    return element.alt || 'Image';
  }

  // For inputs, use value or placeholder
  if (element.tagName.toLowerCase() === 'input') {
    return element.value || element.placeholder || element.type || 'Input';
  }

  // Get text content and clean it up
  let text = element.textContent || element.innerText || '';
  text = text.trim().replace(/\s+/g, ' ');

  // Limit length to avoid huge strings
  return text.length > 100 ? text.substring(0, 100) + '...' : text;
};

/**
 * Get parent component name by traversing up the DOM
 * @param {Element} element - Starting DOM element
 * @returns {string} Best guess at parent component name
 */
export const getParentComponent = (element) => {
  let current = element;
  let depth = 0;
  const maxDepth = 10;

  while (current && current.parentElement && depth < maxDepth) {
    current = current.parentElement;
    depth++;

    // Look for React component indicators
    const classes = typeof current.className === 'string' ? current.className : (current.className?.baseVal || '');

    // Check for component-like class names
    const componentPatterns = [
      /^[A-Z][a-zA-Z]*/, // PascalCase
      /-component$/,
      /-container$/,
      /-wrapper$/,
      /^filter-/,
      /^event-/,
      /^modal-/,
      /^sidebar-/
    ];

    for (const pattern of componentPatterns) {
      const matches = classes?.split(' ')?.filter(cls => pattern.test(cls));
      if (matches.length > 0) {
        return matches[0];
      }
    }

    // Check for data attributes that might indicate components
    if (current.dataset && current.dataset.component) {
      return current.dataset.component;
    }
  }

  return 'Unknown';
};

/**
 * Collect comprehensive metadata about a click event
 * @param {Event} event - Click event
 * @param {Element} element - Clicked element
 * @returns {Object} Comprehensive metadata object
 */
export const collectClickMetadata = (event, element) => {
  return {
    elementType: element.tagName.toLowerCase(),
    elementText: getElementText(element),
    elementId: element.id || null,
    elementClasses: element.className || null,
    pagePath: window.location.pathname + window.location.search,
    pageTitle: document.title,
    parentComponent: getParentComponent(element),
    clickCoordinates: {
      x: event.clientX,
      y: event.clientY,
      pageX: event.pageX,
      pageY: event.pageY
    },
    viewportSize: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    userAgent: navigator.userAgent,
    metadata: {
      timestamp: new Date().toISOString(),
      href: element.href || null,
      type: element.type || null,
      name: element.name || null,
      value: element.value || null,
      disabled: element.disabled || false,
      target: element.target || null,
      dataAttributes: Object.keys(element.dataset || {}).reduce((acc, key) => {
        acc[key] = element.dataset[key];
        return acc;
      }, {})
    }
  };
};